import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Group, Progress, Stack, Text, Badge } from '@mantine/core';
import dayjs from 'dayjs';

import { GET_HISTORICAL_OVERRIDE_ACTIVE_IMPORTS } from '../../apollo/queries/getHistoricalOverrideActiveImports';
import { ImportType } from '../../types/import';

interface ProgressMetadata {
    phase?: string;
    message?: string;
    total?: number;
    sent?: number;
    percent?: number;
    updatedAt?: string;
}

interface ActiveImportLog {
    id: string;
    fileName: string;
    status: string;
    startedAt?: string | null;
    recordsProcessed?: number | null;
    metadata?: { progress?: ProgressMetadata } | null;
    createdBy?: {
        id: string;
        firstName: string;
        lastName: string;
    } | null;
}

const PHASE_FALLBACK_LABELS: Record<string, string> = {
    downloading: 'Downloading workbook…',
    staging: 'Streaming workbook into staging…',
    sending: 'Queueing records…',
    finalizing: 'Finalising…',
};

interface Props {
    readonly onActiveChange?: (hasActive: boolean) => void;
}

export function HistoricalOverrideActiveImportsTable({
    onActiveChange,
}: Props): JSX.Element | null {
    const { data, refetch } = useQuery(GET_HISTORICAL_OVERRIDE_ACTIVE_IMPORTS, {
        variables: {
            input: {
                limit: 20,
                offset: 0,
                importType: ImportType.DailySalesHistoricalManualOverride,
                statuses: ['Processing'],
            },
        },
        pollInterval: 3000,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                refetch().catch(() => undefined);
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [refetch]);

    const logs: ActiveImportLog[] = data?.getManualImportLogs?.logs ?? [];

    useEffect(() => {
        onActiveChange?.(logs.length > 0);
    }, [logs.length, onActiveChange]);

    if (logs.length === 0) {
        return null;
    }

    return (
        <Stack gap="xs">
            <Text size="sm" fw={600} c="dimmed">
                In progress
            </Text>
            {logs.map(log => {
                const progress = log.metadata?.progress;
                const phase = progress?.phase ?? '';
                const phaseLabel =
                    progress?.message ||
                    PHASE_FALLBACK_LABELS[phase] ||
                    'Initializing…';
                const total = progress?.total ?? 0;
                const sent = progress?.sent ?? 0;
                const percent =
                    progress?.percent ??
                    (total > 0 ? Math.min(100, (sent / total) * 100) : 0);

                const detail =
                    total > 0
                        ? `${sent.toLocaleString()} of ${total.toLocaleString()} (${Math.floor(
                              percent,
                          )}%)`
                        : null;

                return (
                    <Box
                        key={log.id}
                        className="border border-gray-200 rounded-lg p-3 bg-white"
                    >
                        <Group justify="space-between" mb={4}>
                            <Group gap="xs">
                                <Text fw={500} size="sm">
                                    {log.fileName}
                                </Text>
                                <Badge color="blue" variant="light" size="xs">
                                    {log.status}
                                </Badge>
                            </Group>
                            {log.startedAt && (
                                <Text size="xs" c="dimmed">
                                    Started{' '}
                                    {dayjs(log.startedAt).format('HH:mm')}
                                </Text>
                            )}
                        </Group>
                        <Text size="xs" c="dimmed" mb={6}>
                            {phaseLabel}
                            {detail ? ` — ${detail}` : ''}
                        </Text>
                        <Progress
                            value={percent}
                            size="sm"
                            radius="xl"
                            striped
                            animated={total > 0 && sent < total}
                        />
                    </Box>
                );
            })}
        </Stack>
    );
}
